import { useState, useEffect, useCallback } from "react";
import { useAuth, api } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ClipboardCheck, Play, CheckCircle2, XCircle, FileDown, History, Search,
  AlertCircle, Loader2, Plus, Minus,
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const InventurPage = () => {
  const { user } = useAuth();
  const [activeInventur, setActiveInventur] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [startNotes, setStartNotes] = useState("");
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [completeNotes, setCompleteNotes] = useState("");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [historyDetail, setHistoryDetail] = useState(null);
  const [companySettings, setCompanySettings] = useState({});

  const isAdmin = user?.role === "admin";

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cur, hist, settings] = await Promise.all([
        api.get("/api/inventur/current"),
        api.get("/api/inventur"),
        api.get("/api/company-settings").catch(() => ({ data: {} })),
      ]);
      setActiveInventur(cur.data);
      setHistory(hist.data || []);
      setCompanySettings(settings.data || {});
    } catch (err) {
      toast.error("Fehler beim Laden der Inventur");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleStart = async () => {
    try {
      const res = await api.post("/api/inventur/start", { notes: startNotes });
      setActiveInventur(res.data);
      setStartDialogOpen(false);
      setStartNotes("");
      toast.success(`Inventur gestartet (${res.data.items?.length || 0} Artikel)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Starten");
    }
  };

  const handleCount = async (materialId, value) => {
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) return;
    try {
      await api.patch(`/api/inventur/items/${materialId}`, { bestand_ist: num });
      // Optimistic local update
      setActiveInventur(prev => {
        if (!prev) return prev;
        const items = prev.items.map(it =>
          it.material_id === materialId
            ? { ...it, bestand_ist: num, counted: true, counted_at: new Date().toISOString() }
            : it
        );
        const totals = calcTotals(items);
        return { ...prev, items, ...totals };
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleComplete = async () => {
    try {
      const res = await api.post("/api/inventur/complete", { apply_corrections: true, notes: completeNotes });
      toast.success(`Inventur abgeschlossen (${res.data.corrections_applied || 0} Bestandskorrekturen)`);
      // Generate PDF automatically
      generatePDF(res.data);
      setCompleteDialogOpen(false);
      setCompleteNotes("");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Abschließen");
    }
  };

  const handleCancel = async () => {
    try {
      await api.post("/api/inventur/cancel");
      toast.success("Inventur abgebrochen");
      setCancelDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Abbrechen");
    }
  };

  const calcTotals = (items) => {
    let total_wert_soll = 0, total_wert_ist = 0, items_counted = 0;
    items.forEach(it => {
      const ek = parseFloat(it.einkaufspreis) || 0;
      total_wert_soll += (parseFloat(it.bestand_soll) || 0) * ek;
      if (it.bestand_ist != null) {
        total_wert_ist += parseFloat(it.bestand_ist) * ek;
        items_counted += 1;
      }
    });
    return {
      total_wert_soll: Math.round(total_wert_soll * 100) / 100,
      total_wert_ist: Math.round(total_wert_ist * 100) / 100,
      items_total: items.length,
      items_counted,
    };
  };

  const generatePDF = (inv) => {
    if (!inv) return;
    const doc = new jsPDF();
    const companyName = companySettings?.company_name || "Werkstatt";
    const date = new Date(inv.completed_at || inv.started_at).toLocaleDateString("de-DE");

    doc.setFontSize(18);
    doc.text("Inventurliste", 14, 18);
    doc.setFontSize(10);
    doc.text(`${companyName} · Stichtag: ${date}`, 14, 25);
    doc.text(`Status: ${inv.status === "completed" ? "Abgeschlossen" : inv.status}`, 14, 31);
    if (inv.completed_by_name) doc.text(`Erfasst durch: ${inv.completed_by_name}`, 14, 37);

    const body = (inv.items || []).map(it => {
      const ist = it.bestand_ist != null ? it.bestand_ist : "—";
      const diff = it.bestand_ist != null ? (it.bestand_ist - it.bestand_soll) : 0;
      const wertIst = (it.bestand_ist != null ? it.bestand_ist : 0) * (it.einkaufspreis || 0);
      return [
        it.bezeichnung || "",
        it.artikelnummer || "",
        it.hersteller || "",
        `${it.bestand_soll} ${it.einheit || ""}`,
        ist !== "—" ? `${ist} ${it.einheit || ""}` : "—",
        diff === 0 ? "0" : (diff > 0 ? `+${diff}` : `${diff}`),
        `${(it.einkaufspreis || 0).toFixed(2)} €`,
        `${wertIst.toFixed(2)} €`,
      ];
    });

    autoTable(doc, {
      startY: 44,
      head: [["Bezeichnung", "Art.Nr.", "Hersteller", "Soll", "Ist", "Diff.", "EK/Stk", "Wert Ist"]],
      body,
      foot: [[
        "Gesamt", "", "", "", "", "",
        `${(inv.total_wert_soll || 0).toFixed(2)} € (Soll)`,
        `${(inv.total_wert_ist || 0).toFixed(2)} € (Ist)`,
      ]],
      headStyles: { fillColor: [59, 130, 246], fontSize: 9, fontStyle: "bold" },
      footStyles: { fillColor: [226, 232, 240], textColor: 0, fontStyle: "bold", fontSize: 9 },
      bodyStyles: { fontSize: 8 },
      margin: { left: 10, right: 10 },
      columnStyles: {
        3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" },
        6: { halign: "right" }, 7: { halign: "right" },
      },
    });

    if (inv.notes) {
      const finalY = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(9);
      doc.text("Notizen:", 14, finalY);
      doc.text(inv.notes, 14, finalY + 5, { maxWidth: 180 });
    }

    doc.save(`inventur_${date.replaceAll(".", "-")}.pdf`);
  };

  const handleDownloadHistory = async (id) => {
    try {
      const res = await api.get(`/api/inventur/${id}`);
      generatePDF(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Inventur");
    }
  };

  const filteredItems = (activeInventur?.items || []).filter(it => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (it.bezeichnung || "").toLowerCase().includes(s) ||
      (it.artikelnummer || "").toLowerCase().includes(s) ||
      (it.hersteller || "").toLowerCase().includes(s)
    );
  });

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="inventur-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 style={{ fontFamily: "Barlow Condensed" }} className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <ClipboardCheck className="w-8 h-8 text-primary" />
            Inventur
          </h1>
          <p className="text-muted-foreground text-sm">Bestandsaufnahme für Buchhaltung &amp; Finanzamt</p>
        </div>
        {!activeInventur && isAdmin && (
          <Button onClick={() => setStartDialogOpen(true)} className="gap-2" data-testid="start-inventur-btn">
            <Play className="w-4 h-4" /> Neue Inventur starten
          </Button>
        )}
      </div>

      {!activeInventur ? (
        <div className="border-2 border-dashed rounded-xl p-10 text-center">
          <ClipboardCheck className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="font-medium">Keine aktive Inventur</p>
          <p className="text-sm text-muted-foreground mt-1">
            {isAdmin ? "Starte eine neue Inventur, um die Bestände aller Artikel zu erfassen." : "Nur Administratoren können eine Inventur starten."}
          </p>
        </div>
      ) : (
        <ActiveInventurView
          inv={activeInventur}
          filteredItems={filteredItems}
          search={search}
          setSearch={setSearch}
          onCount={handleCount}
          onComplete={() => setCompleteDialogOpen(true)}
          onCancel={() => setCancelDialogOpen(true)}
          isAdmin={isAdmin}
        />
      )}

      {/* History */}
      <div className="border rounded-xl">
        <div className="p-4 border-b flex items-center gap-2">
          <History className="w-5 h-5 text-muted-foreground" />
          <h2 className="font-semibold">Historie</h2>
          <span className="text-xs text-muted-foreground ml-1">({history.length})</span>
        </div>
        {history.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground text-center">Noch keine Inventuren abgeschlossen.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">Datum</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-right p-3 font-medium hidden sm:table-cell">Artikel</th>
                  <th className="text-right p-3 font-medium">Wert Soll</th>
                  <th className="text-right p-3 font-medium">Wert Ist</th>
                  <th className="text-left p-3 font-medium hidden md:table-cell">Erfasst durch</th>
                  <th className="text-right p-3 font-medium">PDF</th>
                </tr>
              </thead>
              <tbody>
                {history.map(h => {
                  const date = new Date(h.completed_at || h.started_at).toLocaleDateString("de-DE");
                  const time = new Date(h.completed_at || h.started_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
                  return (
                    <tr key={h.id} className="border-t hover:bg-muted/30" data-testid="history-row">
                      <td className="p-3">{date}<span className="text-muted-foreground"> · {time}</span></td>
                      <td className="p-3">
                        {h.status === "completed" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                            <CheckCircle2 className="w-3 h-3" /> Abgeschlossen
                          </span>
                        ) : h.status === "cancelled" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
                            <XCircle className="w-3 h-3" /> Abgebrochen
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                            Aktiv
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-right hidden sm:table-cell">{h.items_total ?? 0}</td>
                      <td className="p-3 text-right">{(h.total_wert_soll || 0).toFixed(2)} €</td>
                      <td className="p-3 text-right font-medium">{(h.total_wert_ist || 0).toFixed(2)} €</td>
                      <td className="p-3 text-muted-foreground hidden md:table-cell">{h.completed_by_name || h.started_by_name || "—"}</td>
                      <td className="p-3 text-right">
                        {h.status === "completed" && (
                          <Button variant="ghost" size="sm" onClick={() => handleDownloadHistory(h.id)} className="gap-1" data-testid="download-history-pdf">
                            <FileDown className="w-3.5 h-3.5" /> PDF
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Start dialog */}
      <Dialog open={startDialogOpen} onOpenChange={setStartDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neue Inventur starten</DialogTitle>
            <DialogDescription>
              Während die Inventur läuft, sind alle Lagerbewegungen (Material anlegen, ändern, Auftragsmaterial buchen) gesperrt – damit das Ergebnis nicht verfälscht wird.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Notiz (optional)</Label>
            <Textarea
              value={startNotes}
              onChange={(e) => setStartNotes(e.target.value)}
              placeholder="z.B. Jahresinventur 31.12., Halbjahresinventur, ..."
              data-testid="inventur-notes-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStartDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleStart} data-testid="confirm-start-inventur">Inventur starten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete dialog */}
      <Dialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Inventur abschließen</DialogTitle>
            <DialogDescription>
              Die gezählten Bestände werden auf die Lagerartikel angewendet (Bestände werden korrigiert). Anschließend wird automatisch ein PDF erzeugt und in der Historie gespeichert.
            </DialogDescription>
          </DialogHeader>
          {activeInventur && (
            <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
              <div className="flex justify-between"><span>Erfasste Artikel:</span><span className="font-medium">{activeInventur.items_counted} / {activeInventur.items_total}</span></div>
              <div className="flex justify-between"><span>Wert (Soll):</span><span className="font-medium">{(activeInventur.total_wert_soll || 0).toFixed(2)} €</span></div>
              <div className="flex justify-between"><span>Wert (Ist):</span><span className="font-medium">{(activeInventur.total_wert_ist || 0).toFixed(2)} €</span></div>
            </div>
          )}
          <div className="space-y-2">
            <Label>Abschlussnotiz (optional)</Label>
            <Textarea value={completeNotes} onChange={(e) => setCompleteNotes(e.target.value)} placeholder="z.B. Differenzen geklärt, ..." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleComplete} className="gap-2" data-testid="confirm-complete-inventur">
              <CheckCircle2 className="w-4 h-4" /> Abschließen &amp; PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inventur abbrechen?</AlertDialogTitle>
            <AlertDialogDescription>
              Bereits eingegebene Zählungen werden verworfen. Die Bestände im Lager bleiben unverändert. Die Sperre für Lagerbewegungen wird wieder aufgehoben.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Zurück</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancel} className="bg-red-600 hover:bg-red-700">Inventur abbrechen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const ActiveInventurView = ({ inv, filteredItems, search, setSearch, onCount, onComplete, onCancel, isAdmin }) => {
  return (
    <>
      <div className="border-2 border-blue-500/40 bg-blue-50/40 rounded-xl p-4" data-testid="active-inventur-banner">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600" />
              <span className="font-semibold">Inventur läuft</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Gestartet: {new Date(inv.started_at).toLocaleString("de-DE")} · von {inv.started_by_name}
            </p>
            {inv.notes && <p className="text-xs mt-1">{inv.notes}</p>}
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={onCancel} className="gap-2" data-testid="cancel-inventur-btn">
                <XCircle className="w-4 h-4" /> Abbrechen
              </Button>
              <Button onClick={onComplete} className="gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="complete-inventur-btn">
                <CheckCircle2 className="w-4 h-4" /> Abschließen
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Artikel gesamt" value={inv.items_total} />
        <Stat label="Erfasst" value={`${inv.items_counted} / ${inv.items_total}`} highlight={inv.items_counted === inv.items_total} />
        <Stat label="Wert Soll" value={`${(inv.total_wert_soll || 0).toFixed(2)} €`} />
        <Stat label="Wert Ist" value={`${(inv.total_wert_ist || 0).toFixed(2)} €`} />
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder="Suchen: Bezeichnung, Art.Nr., Hersteller..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="inventur-search" />
      </div>

      <div className="border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">Bezeichnung</th>
                <th className="text-left p-3 font-medium hidden md:table-cell">Art.Nr.</th>
                <th className="text-right p-3 font-medium">Soll</th>
                <th className="text-right p-3 font-medium">Ist (gezählt)</th>
                <th className="text-right p-3 font-medium">Diff.</th>
                <th className="text-right p-3 font-medium hidden sm:table-cell">EK</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map(it => {
                const ist = it.bestand_ist;
                const diff = ist != null ? ist - it.bestand_soll : null;
                return (
                  <tr key={it.material_id} className={`border-t hover:bg-muted/30 ${it.counted ? 'bg-emerald-50/30' : ''}`} data-testid="inventur-item-row">
                    <td className="p-3">
                      <div className="font-medium">{it.bezeichnung}</div>
                      <div className="text-xs text-muted-foreground">{it.hersteller || it.lieferant || ""}</div>
                    </td>
                    <td className="p-3 text-muted-foreground hidden md:table-cell">{it.artikelnummer || "—"}</td>
                    <td className="p-3 text-right">{it.bestand_soll} {it.einheit}</td>
                    <td className="p-3 text-right">
                      <CountInput initial={ist} onSubmit={(v) => onCount(it.material_id, v)} />
                    </td>
                    <td className={`p-3 text-right font-medium ${diff === 0 ? 'text-muted-foreground' : diff > 0 ? 'text-emerald-600' : diff < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                      {ist == null ? "—" : diff === 0 ? "0" : diff > 0 ? `+${diff}` : `${diff}`}
                    </td>
                    <td className="p-3 text-right hidden sm:table-cell">{(it.einkaufspreis || 0).toFixed(2)} €</td>
                  </tr>
                );
              })}
              {filteredItems.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Keine Artikel gefunden.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

const Stat = ({ label, value, highlight }) => (
  <div className={`border rounded-xl p-4 ${highlight ? 'bg-emerald-50 border-emerald-300' : 'bg-card'}`}>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-xl font-bold mt-1 ${highlight ? 'text-emerald-700' : ''}`}>{value}</p>
  </div>
);

const CountInput = ({ initial, onSubmit }) => {
  const [val, setVal] = useState(initial != null ? String(initial) : "");

  useEffect(() => {
    setVal(initial != null ? String(initial) : "");
  }, [initial]);

  const commit = () => {
    if (val === "" || val == null) return;
    onSubmit(val);
  };

  const adjust = (delta) => {
    const cur = parseFloat(val) || 0;
    const next = Math.max(0, cur + delta);
    setVal(String(next));
    onSubmit(next);
  };

  return (
    <div className="flex items-center gap-1 justify-end">
      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => adjust(-1)} data-testid="count-minus">
        <Minus className="w-3 h-3" />
      </Button>
      <Input
        type="number"
        step="any"
        min="0"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") { e.target.blur(); } }}
        className="h-8 w-20 text-right"
        placeholder="—"
        data-testid="count-input"
      />
      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => adjust(1)} data-testid="count-plus">
        <Plus className="w-3 h-3" />
      </Button>
    </div>
  );
};

export default InventurPage;
